import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Row, Col } from 'react-bootstrap';

interface ManualDateInputProps {
  onDateChange: (date: string) => void;
  initialDate?: string;
  disabled?: boolean;
}

const getTodayString = (): string => {
  const currentDate = new Date();
  return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(
    currentDate.getDate()
  ).padStart(2, '0')}`;
};

export const ManualDateInput: React.FC<ManualDateInputProps> = ({
  onDateChange,
  initialDate,
  disabled = false,
}) => {
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [datePickerValue, setDatePickerValue] = useState<string>('');
  const [errors, setErrors] = useState<{ year?: string; month?: string; day?: string }>({});

  const lastEmittedDateRef = useRef<string>('');

  // Synchronize internal state when initialDate arrives or changes
  useEffect(() => {
    const targetDate = initialDate && initialDate.trim() !== '' ? initialDate : getTodayString();
    if (targetDate === lastEmittedDateRef.current) {
      return;
    }

    const parts = targetDate.split('-');
    if (parts.length === 3) {
      setYear(parts[0]);
      setMonth(parts[1].padStart(2, '0'));
      setDay(parts[2].padStart(2, '0'));
      setDatePickerValue(targetDate);
      lastEmittedDateRef.current = targetDate;
      setErrors({});
    }
  }, [initialDate]);

  const validateDate = (y: string, m: string, d: string): boolean => {
    const newErrors: { year?: string; month?: string; day?: string } = {};

    if (!y || !m || !d) {
      setErrors({});
      return false;
    }

    const yearNum = parseInt(y, 10);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > 2100) {
      newErrors.year = 'Please select a valid year';
    }

    const monthNum = parseInt(m, 10);
    if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      newErrors.month = 'Please select a valid month';
    }

    const dayNum = parseInt(d, 10);
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      newErrors.day = 'Please select a valid day';
    }

    if (!newErrors.year && !newErrors.month && !newErrors.day) {
      const date = new Date(yearNum, monthNum - 1, dayNum);
      if (
        date.getFullYear() !== yearNum ||
        date.getMonth() !== monthNum - 1 ||
        date.getDate() !== dayNum
      ) {
        newErrors.day = 'Invalid date for selected month/year';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const emitDateChange = (y: string, m: string, d: string) => {
    const formattedMonth = m.padStart(2, '0');
    const formattedDay = d.padStart(2, '0');
    const dateStr = `${y}-${formattedMonth}-${formattedDay}`;

    if (validateDate(y, formattedMonth, formattedDay)) {
      setDatePickerValue(dateStr);
      lastEmittedDateRef.current = dateStr;
      onDateChange(dateStr);
    }
  };

  const handleDatePickerChange = (newVal: string) => {
    if (!newVal) return;
    const parts = newVal.split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      setYear(y);
      setMonth(m.padStart(2, '0'));
      setDay(d.padStart(2, '0'));
      setDatePickerValue(newVal);
      setErrors({});
      lastEmittedDateRef.current = newVal;
      onDateChange(newVal);
    }
  };

  const handleYearChange = (newYear: string) => {
    setYear(newYear);
    if (newYear && month && day) {
      emitDateChange(newYear, month, day);
    }
  };

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth);
    if (year && newMonth) {
      let currentDay = day;
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(newMonth, 10);
      if (!isNaN(yearNum) && !isNaN(monthNum)) {
        const maxDays = new Date(yearNum, monthNum, 0).getDate();
        if (currentDay && parseInt(currentDay, 10) > maxDays) {
          currentDay = String(maxDays).padStart(2, '0');
          setDay(currentDay);
        }
      }
      if (currentDay) {
        emitDateChange(year, newMonth, currentDay);
      }
    }
  };

  const handleDayChange = (newDay: string) => {
    setDay(newDay);
    if (year && month && newDay) {
      emitDateChange(year, month, newDay);
    }
  };

  const handleSetToday = () => {
    const today = getTodayString();
    const parts = today.split('-');
    if (parts.length === 3) {
      setYear(parts[0]);
      setMonth(parts[1]);
      setDay(parts[2]);
      setDatePickerValue(today);
      setErrors({});
      lastEmittedDateRef.current = today;
      onDateChange(today);
    }
  };

  // Generate year options (current year - 10 to current year + 5)
  const generateYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear - 10; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  }, []);

  const monthOptions = useMemo(
    () => [
      { value: '01', label: '01 - January' },
      { value: '02', label: '02 - February' },
      { value: '03', label: '03 - March' },
      { value: '04', label: '04 - April' },
      { value: '05', label: '05 - May' },
      { value: '06', label: '06 - June' },
      { value: '07', label: '07 - July' },
      { value: '08', label: '08 - August' },
      { value: '09', label: '09 - September' },
      { value: '10', label: '10 - October' },
      { value: '11', label: '11 - November' },
      { value: '12', label: '12 - December' },
    ],
    []
  );

  const dayOptions = useMemo(() => {
    let maxDays = 31;
    if (year && month) {
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      if (!isNaN(yearNum) && !isNaN(monthNum)) {
        maxDays = new Date(yearNum, monthNum, 0).getDate();
      }
    }
    const days: string[] = [];
    for (let i = 1; i <= maxDays; i++) {
      days.push(String(i).padStart(2, '0'));
    }
    return days;
  }, [year, month]);

  const formattedDateDisplay = useMemo(() => {
    if (!year || !month || !day) return '';
    try {
      const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-AU', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      }
    } catch {
      return '';
    }
    return '';
  }, [year, month, day]);

  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px' }}>
      {/* Calendar Date Picker & Quick Actions Row */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', minWidth: '170px' }}>
          <Form.Group controlId="manual-date-picker-input">
            <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>📅</span> Calendar Picker
            </Form.Label>
            <Form.Control
              type="date"
              aria-label="Calendar Picker"
              value={datePickerValue}
              onChange={(e) => handleDatePickerChange(e.target.value)}
              disabled={disabled}
              style={{
                borderRadius: '8px',
                borderColor: '#D1D5DB',
                fontSize: '0.9rem',
                padding: '7px 12px',
                backgroundColor: '#FFFFFF',
              }}
            />
          </Form.Group>
        </div>

        <button
          type="button"
          onClick={handleSetToday}
          disabled={disabled}
          className="btn btn-outline-secondary"
          style={{
            borderRadius: '8px',
            padding: '7px 14px',
            fontSize: '0.82rem',
            fontWeight: 600,
            height: '38px',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: '#FFFFFF',
            borderColor: '#D1D5DB',
            color: '#374151',
          }}
          title="Reset to today's date"
        >
          ↺ Set to Today
        </button>

        {formattedDateDisplay && (
          <div
            style={{
              padding: '6px 14px',
              background: 'rgba(37, 99, 235, 0.08)',
              border: '1px solid rgba(37, 99, 235, 0.2)',
              borderRadius: '8px',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#1D4ED8',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              height: '38px',
            }}
          >
            <span>Date:</span>
            <span>{formattedDateDisplay}</span>
          </div>
        )}
      </div>

      {/* Dropdown controls */}
      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', fontWeight: 600, marginBottom: '8px' }}>
          Or Select via Dropdowns
        </div>

        <Row className="g-2">
          {/* Year */}
          <Col xs={12} sm={4}>
            <Form.Group controlId="manual-date-year-select">
              <Form.Label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4B5563', marginBottom: '3px' }}>
                Year
              </Form.Label>
              <Form.Select
                aria-label="Year"
                value={year}
                onChange={(e) => handleYearChange(e.target.value)}
                disabled={disabled}
                isInvalid={!!errors.year}
                style={{ borderRadius: '8px', fontSize: '0.88rem', backgroundColor: '#FFFFFF' }}
              >
                <option value="">Select Year</option>
                {generateYearOptions.map((yearOption) => (
                  <option key={yearOption} value={String(yearOption)}>
                    {yearOption}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.year}</Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Month */}
          <Col xs={12} sm={4}>
            <Form.Group controlId="manual-date-month-select">
              <Form.Label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4B5563', marginBottom: '3px' }}>
                Month
              </Form.Label>
              <Form.Select
                aria-label="Month"
                value={month}
                onChange={(e) => handleMonthChange(e.target.value)}
                disabled={disabled}
                isInvalid={!!errors.month}
                style={{ borderRadius: '8px', fontSize: '0.88rem', backgroundColor: '#FFFFFF' }}
              >
                <option value="">Select Month</option>
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.month}</Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Day */}
          <Col xs={12} sm={4}>
            <Form.Group controlId="manual-date-day-select">
              <Form.Label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4B5563', marginBottom: '3px' }}>
                Day
              </Form.Label>
              <Form.Select
                aria-label="Day"
                value={day}
                onChange={(e) => handleDayChange(e.target.value)}
                disabled={disabled}
                isInvalid={!!errors.day}
                style={{ borderRadius: '8px', fontSize: '0.88rem', backgroundColor: '#FFFFFF' }}
              >
                <option value="">Select Day</option>
                {dayOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.day}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </div>
    </div>
  );
};
